import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { useClient, useMutation } from "urql"

import { sendAmplitudeData } from "../../support/amplitude-react"
import {
  applyEmailHeuristic,
  blockingRowCount,
  canTransformClientImportWithAi,
  computeEmailValid,
  type ClientImportReviewRowState
} from "../../utils/cleanClientImportRow"
import { chunkArray, inviteClientsInBatches, type InviteImportProgress } from "../../utils/inviteClientsInBatches"

import * as ButtonsModule from "./Buttons"
import * as ModalModule from "./Modal"

type AnyProps = Record<string, unknown> & { children?: React.ReactNode }
const AnimatedModal = ModalModule.AnimatedModal as unknown as React.FC<AnyProps>
const Button = ButtonsModule.Button as unknown as React.FC<AnyProps>

const PREVIEW_QUERY = `
  query PreviewClientImportContacts($rows: [ClientImportPreviewRowInput!]!) {
    previewClientImportContacts(rows: $rows) {
      rows {
        rowKey
        emailValid
        phoneValid
      }
    }
  }
`

const CLEAN_MUTATION = `
  mutation CleanClientImportContacts($cells: [ClientImportCellInput!]!) {
    cleanClientImportContacts(cells: $cells) {
      llmStatus
      cells {
        rowKey
        field
        value
        valid
        notes
      }
    }
  }
`

const INVITE_MUTATION = `
  mutation InviteClients($clients: [ClientInput!]!) {
    inviteClients(clients: $clients) {
      erroredClients {
        email
      }
    }
  }
`

const CLEAN_CHUNK_SIZE = 40
const PHONE_PREVIEW_DEBOUNCE_MS = 400

type PreviewRowPayload = {
  rowKey: string
  emailValid: boolean
  phoneValid: boolean
}

export type ClientImportReviewInitialRow = {
  firstName: string
  lastName: string
  email: string
  phone: string
  notes: string
}

type ClientImportReviewProps = {
  initialRows: ClientImportReviewInitialRow[]
  collapsedByEmail?: number
  visible: boolean
  onCancel: () => void
  onImported: (summary: { importedOk: number; inviteErrors: number; totalRows: number }) => void
  onImportProgress: (progress: InviteImportProgress | null) => void
}

function buildInitialRows(initialRows: ClientImportReviewInitialRow[]): ClientImportReviewRowState[] {
  return initialRows.map((row) => ({
    rowKey: crypto.randomUUID(),
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email,
    phone: row.phone,
    notes: row.notes,
    emailValid: computeEmailValid(row.email),
    phoneValid: false,
    phoneCheck: "dirty"
  }))
}

function contactInputClass(valid: boolean, value: string): string {
  const invalid = value.trim() !== "" && !valid
  return twMerge(
    "w-full min-w-0 rounded border border-gray-light px-2 py-1 text-sm",
    invalid && "ring-red-300 bg-red-50 ring-1"
  )
}

export default function ClientImportReview({
  initialRows,
  collapsedByEmail = 0,
  visible,
  onCancel,
  onImported,
  onImportProgress
}: ClientImportReviewProps) {
  const urqlClient = useClient()
  const [, cleanContacts] = useMutation(CLEAN_MUTATION)
  const [, inviteClients] = useMutation(INVITE_MUTATION)

  const [rows, setRows] = useState<ClientImportReviewRowState[]>(() => buildInitialRows(initialRows))
  const [previewLoading, setPreviewLoading] = useState(true)
  const [fixing, setFixing] = useState(false)
  const [importing, setImporting] = useState(false)
  const [llmNotice, setLlmNotice] = useState<string | null>(null)
  const phonePreviewTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const blockingCount = useMemo(() => blockingRowCount(rows), [rows])
  const canTransform = canTransformClientImportWithAi({
    rowCount: rows.length,
    previewLoading,
    fixing,
    importing
  })

  const runPreview = useCallback(
    async (targetRows: ClientImportReviewRowState[]) => {
      if (targetRows.length === 0) return
      const result = await urqlClient
        .query(
          PREVIEW_QUERY,
          {
            rows: targetRows.map((r) => ({
              rowKey: r.rowKey,
              email: r.email,
              phone: r.phone
            }))
          },
          { requestPolicy: "network-only" }
        )
        .toPromise()
      const previewRows = (result.data?.previewClientImportContacts?.rows ?? []) as PreviewRowPayload[]
      const byKey = new Map(previewRows.map((r) => [r.rowKey, r]))
      setRows((prev) =>
        prev.map((row) => {
          const hit = byKey.get(row.rowKey)
          if (!hit) return row
          return {
            ...row,
            emailValid: hit.emailValid,
            phoneValid: hit.phoneValid,
            phoneCheck: "clean"
          }
        })
      )
    },
    [urqlClient]
  )

  useEffect(() => {
    if (!visible) return
    const built = buildInitialRows(initialRows)
    setRows(built)
    setLlmNotice(null)
    setPreviewLoading(true)
    void runPreview(built).finally(() => setPreviewLoading(false))
  }, [visible, initialRows, runPreview])

  const schedulePhonePreview = useCallback(
    (nextRows: ClientImportReviewRowState[]) => {
      if (phonePreviewTimer.current) clearTimeout(phonePreviewTimer.current)
      phonePreviewTimer.current = setTimeout(() => {
        phonePreviewTimer.current = null
        const dirty = nextRows.filter((r) => r.phoneCheck === "dirty")
        if (dirty.length === 0) return
        setPreviewLoading(true)
        void runPreview(dirty).finally(() => setPreviewLoading(false))
      }, PHONE_PREVIEW_DEBOUNCE_MS)
    },
    [runPreview]
  )

  const updateRow = (rowKey: string, patch: Partial<ClientImportReviewRowState>) => {
    setRows((prev) => {
      const next = prev.map((row) => {
        if (row.rowKey !== rowKey) return row
        const merged = { ...row, ...patch }
        if (patch.email !== undefined) {
          merged.emailValid = computeEmailValid(merged.email)
        }
        if (patch.phone !== undefined) {
          merged.phoneCheck = "dirty"
          merged.phoneValid = merged.phone.trim() === "" ? true : false
        }
        return merged
      })
      if (patch.phone !== undefined) schedulePhonePreview(next)
      return next
    })
  }

  const removeRow = (rowKey: string) => {
    setRows((prev) => prev.filter((r) => r.rowKey !== rowKey))
  }

  const handleTransformWithAi = async () => {
    setFixing(true)
    setLlmNotice(null)
    const before = rows.map((row) => `${row.rowKey}|${row.email}|${row.phone}|${row.notes}`).join("\n")
    let working = rows.map((row) => {
      const { email, notes } = applyEmailHeuristic({ email: row.email, notes: row.notes })
      return {
        ...row,
        email,
        notes,
        emailValid: computeEmailValid(email)
      }
    })
    setRows(working)

    const cells: Array<{ rowKey: string; field: "EMAIL" | "PHONE"; value: string; notes?: string }> = []
    for (const row of working) {
      if (row.email.trim() && !row.emailValid) {
        cells.push({ rowKey: row.rowKey, field: "EMAIL", value: row.email, notes: row.notes })
      }
      if (row.phone.trim() && !row.phoneValid) {
        cells.push({ rowKey: row.rowKey, field: "PHONE", value: row.phone })
      }
    }

    let llmUnavailable = false
    if (cells.length > 0) {
      for (const chunk of chunkArray(cells, CLEAN_CHUNK_SIZE)) {
        const result = await cleanContacts({ cells: chunk })
        if (result.error) break
        const cleaned = result.data?.cleanClientImportContacts
        if (cleaned?.llmStatus === "UNAVAILABLE") llmUnavailable = true
        for (const cell of cleaned?.cells ?? []) {
          working = working.map((row) => {
            if (row.rowKey !== cell.rowKey) return row
            if (cell.field === "EMAIL") {
              return {
                ...row,
                email: cell.value,
                emailValid: cell.valid,
                notes: cell.notes != null ? cell.notes : row.notes
              }
            }
            return {
              ...row,
              phone: cell.value,
              phoneValid: cell.valid,
              phoneCheck: "clean"
            }
          })
        }
        setRows([...working])
      }
    }

    setPreviewLoading(true)
    await runPreview(working)
    setPreviewLoading(false)
    const after = working.map((row) => `${row.rowKey}|${row.email}|${row.phone}|${row.notes}`).join("\n")
    if (llmUnavailable) {
      setLlmNotice("Transform with AI is unavailable. Correct the highlighted cells or remove those rows.")
    } else if (before === after) {
      setLlmNotice("Nothing to improve. Emails and phones already look fine.")
    }
    setFixing(false)
  }

  const handleImport = async () => {
    if (blockingCount > 0 || previewLoading || fixing) return
    setImporting(true)
    onImportProgress({ completedBatches: 0, batchCount: 0, processedRows: 0, totalRows: rows.length })

    const clients = rows.map((row) => ({
      email: row.email.trim(),
      firstName: row.firstName,
      lastName: row.lastName,
      phone: row.phone.trim(),
      notes: row.notes,
      clientType: "invited" as const,
      invitedFrom: "csv_import"
    }))

    try {
      const { erroredClients, lastError } = await inviteClientsInBatches(
        clients,
        (vars) => inviteClients(vars),
        onImportProgress
      )
      if (lastError) {
        window.alert(`Error importing clients: ${String(lastError)}`)
        return
      }
      const inviteErrors = erroredClients.length
      const importedOk = clients.length - inviteErrors
      sendAmplitudeData("import clients submitted", {
        "Total Rows": clients.length,
        "Rows Sent": clients.length,
        "Skipped Invalid Contact": 0,
        "Errored Imports": inviteErrors,
        "CSVBox reported rows": clients.length
      })
      onImported({ importedOk, inviteErrors, totalRows: clients.length })
    } finally {
      onImportProgress(null)
      setImporting(false)
    }
  }

  const importDisabled = blockingCount > 0 || previewLoading || fixing || importing

  return (
    <AnimatedModal
      visible={visible}
      hideModal={onCancel}
      header="Verify data"
      dialogClassName="!w-[min(100%,72rem)] max-w-6xl"
      showFooter={false}
      hideSaveButton
      onSave={() => undefined}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 border-b border-gray-light pb-3">
        <p className="text-sm text-gray-dark">
          {collapsedByEmail > 0
            ? `${collapsedByEmail} row${
                collapsedByEmail === 1 ? "" : "s"
              } used an email that already appeared, so they were combined. The later row is the one kept. `
            : ""}
          {blockingCount > 0
            ? `${blockingCount} row${
                blockingCount === 1 ? "" : "s"
              } need a valid email or phone. Transform with AI rewrites the cells in this table. You can still edit any cell by hand.`
            : "Transform with AI rewrites the email and phone cells in this table. You can still edit any cell by hand."}
        </p>
        <Button type="secondary" onClick={() => void handleTransformWithAi()} disabled={!canTransform}>
          {fixing ? "Transforming…" : "Transform with AI"}
        </Button>
      </div>
      {llmNotice ? <div className="bg-amber-50 text-amber-900 mb-3 rounded px-3 py-2 text-sm">{llmNotice}</div> : null}
      <div className="max-h-[50vh] overflow-auto border border-gray-light">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-dark">
            <tr>
              <th className="p-2">First name</th>
              <th className="p-2">Last name</th>
              <th className="p-2">Email</th>
              <th className="p-2">Phone</th>
              <th className="p-2">Notes</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.rowKey} className="border-t border-gray-light align-top">
                <td className="px-2 py-1">
                  <input
                    className="w-full min-w-24 rounded border border-gray-light px-2 py-1"
                    value={row.firstName}
                    onChange={(e) => updateRow(row.rowKey, { firstName: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    className="w-full min-w-24 rounded border border-gray-light px-2 py-1"
                    value={row.lastName}
                    onChange={(e) => updateRow(row.rowKey, { lastName: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    className={contactInputClass(row.emailValid, row.email)}
                    value={row.email}
                    onChange={(e) => updateRow(row.rowKey, { email: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    className={contactInputClass(row.phoneValid, row.phone)}
                    value={row.phone}
                    onChange={(e) => updateRow(row.rowKey, { phone: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    className="w-full min-w-32 rounded border border-gray-light px-2 py-1"
                    value={row.notes}
                    onChange={(e) => updateRow(row.rowKey, { notes: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1">
                  <Button type="tertiary" size="small" onClick={() => removeRow(row.rowKey)}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-6 flex flex-wrap justify-end gap-3 border-t border-gray-light pt-4">
        <Button type="tertiary" onClick={onCancel} disabled={importing}>
          Close
        </Button>
        <Button type="primary" onClick={() => void handleImport()} disabled={importDisabled}>
          {importing ? "Importing…" : "Import clients"}
        </Button>
      </div>
    </AnimatedModal>
  )
}
